import * as React from "react";
import { useTranslation } from "react-i18next";
import { XCircleIcon } from "@/src/components/icons";
import { UserStatus } from "@dataconnect/generated";
import { useGetUserCredits } from "@dataconnect/generated/react";
import { User } from "firebase/auth";

interface TrialBannerProps {
  onActivate: () => void;
  user: User | null;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({
  onActivate,
  user,
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = React.useState(true);

  const { data } = useGetUserCredits({ enabled: !!user });
  const userData = data?.user;

  const isCanceled = userData?.status === UserStatus.CANCELED;
  const isTrialOutOfCredits =
    userData?.status === UserStatus.TRIAL &&
    userData?.credits !== undefined &&
    userData.credits <= 0;
  // Urgent states suppress the dismiss button so the call-to-action stays
  // visible until the user re-subscribes or tops up.
  const isUrgent = isCanceled || isTrialOutOfCredits;

  if (
    (!isVisible && !isUrgent) ||
    !user ||
    !userData ||
    (userData.status !== UserStatus.TRIAL &&
      userData.status !== UserStatus.CANCELED)
  ) {
    return null;
  }

  const containerClasses = isUrgent
    ? "bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-100 text-gray-900 w-full px-4 py-3 flex items-center justify-between relative z-50"
    : "bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-100 text-gray-900 w-full px-4 py-3 flex items-center justify-between relative z-50";

  const message = isCanceled
    ? t("trialBanner.subscriptionCancelled")
    : isTrialOutOfCredits
      ? t("trialBanner.outOfCredits")
      : t("trialBanner.message");

  return (
    <div className={containerClasses}>
      <div className="flex items-center justify-center flex-grow gap-3 flex-wrap">
        <span className="font-light text-xs md:text-sm text-center">
          {message}
        </span>
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button
            onClick={onActivate}
            className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors shadow-sm whitespace-nowrap"
          >
            {t("trialBanner.button")}
          </button>
          <a
            href="https://meetings.hubspot.com/p-schlieker/showcase-demo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex justify-center items-center px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-sm whitespace-nowrap"
          >
            {t("settings.bookPersonalMeeting")}
          </a>
        </div>
      </div>
      {!isUrgent && (
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-gray-700 transition-colors p-1 ml-2 flex-shrink-0"
          aria-label={t("common.close")}
        >
          <XCircleIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};
